import { v4 as uuidv4 } from 'uuid';

import { version } from 'external_data';
import { round, ErplyAttributes } from 'utils';
import { REDUX_CLIENTCODE } from 'constants/persistence';
import { ObfuscatedLocalStorage } from 'utils/ObfuscatedLocalStorage';
import { getCardPaymentsForIntegration } from 'reducers/Payments';
import { getLoggedInEmployeeID } from 'reducers/Login';

let refno = null;
let amount = null;
const sendMessage = msg => {
  const clientCode = ObfuscatedLocalStorage.getItem(REDUX_CLIENTCODE);
  const messageToSend = {
    ...msg,
    payload: {
      ...(msg.payload ?? {}),
      clientCode,
      softwareName: 'Erply Brazil POS',
      softwareVersion: version,
    },
  };
  return window.wrapperMessageChannel.port1.postMessage(messageToSend);
};

/** Cancel the most recent payment */
export const cancelPayments = () => async (dispatch, getState) => {
  const currentPayment = getCardPaymentsForIntegration('wrapper')(
    getState(),
  ).find(({ referenceNumber }) => String(referenceNumber) === String(refno));
  const employeeID = getLoggedInEmployeeID(getState()) ?? ''; // selector may return null, add fallback
  sendMessage({
    type: 'payment:cancel',
    requestID: uuidv4(),
    payload: {
      referenceNumber: refno, // The refno of the most recently sent startPayment
      amount,
      employeeID,
      current: true,
      meta: {
        paymentIndex: 0,
        totalPayments: 1,
        isTapToPayPayment: !!currentPayment?.attributes?.isTapToPayPayment,
      },
    },
  });
};
/** Notify the wrapper of a finished sale (guarantee no void of payments before this point) */
export const finished = () => {
  sendMessage({
    type: 'payment:finished',
    requestID: uuidv4(),
    payload: {},
  });
};
/** Cancel/void a specific payment */
export const retractPayment = ({ payment, index, total, employeeID = '' }) => {
  const { amount, cardType, cardNumber } = payment;
  const attributes = new ErplyAttributes(payment.attributes);
  const referenceNumber = attributes.get('refNo');
  const isTapToPayPayment = !!attributes.get('isTapToPayPayment');
  const hostReferenceID = attributes.get('hostReferenceID') ?? '';
  const dateTime = attributes.get('dateTime') ?? '';
  sendMessage({
    type: 'payment:cancel',
    requestID: uuidv4(),
    payload: {
      amount: String(amount),
      referenceNumber: String(referenceNumber),
      transactionType: 'VOID',
      employeeID,
      current: false,
      hostReferenceID,
      dateTime: String(dateTime),
      cardType,
      cardNumber,
      meta: {
        paymentIndex: index,
        totalPayments: total,
        isTapToPayPayment,
      },
    },
  });
};

const sendRegularPayment = ({
  payment,
  employeeID = '',
  customer = '',
  currencyCode = '',
  transactionType = 'SALE',
  invoice,
  index,
  total,
}) => {
  refno = uuidv4();
  amount = payment.amount;
  const isTapToPayPayment = !!payment.attributes?.isTapToPayPayment;
  sendMessage({
    type: 'payment:start',
    requestID: uuidv4(),
    payload: {
      transactionType,
      transactionId: payment.key,
      amount: round(payment.amount, 2),
      currency: currencyCode,
      tenderType: 'CREDIT', // Acceptable values: CREDIT, DEBIT
      employeeID,
      invoiceNumber: String(invoice.number), // generated by POS unique ref number
      customerID: String(customer.customerID),
      softwareName: 'Erply Brazil POS',
      softwareVersion: version,
      referenceNumber: refno,
      ecrRefNum: '1',
      sigSavePath: uuidv4(),
      supportedPartialPayments: true,
      meta: {
        paymentIndex: index,
        totalPayments: total,
        isTapToPayPayment,
      },
    },
  });
};

/** Capture a previously performed PREAUTH payment */
export const sendCapturePayment = ({
  payment,
  employeeID = '',
  customer = '',
  currencyCode = '',
  invoice,
  index,
  total,
}) => {
  refno = payment.referenceNumber || uuidv4();
  amount = payment.amount;
  const isTapToPayPayment = !!payment.attributes?.isTapToPayPayment;
  sendMessage({
    type: 'payment:start',
    requestID: uuidv4(),
    payload: {
      transactionType: 'CAPTURE',
      transactionId: payment.key,
      amount: round(payment.amount, 2),
      currency: currencyCode,
      tenderType: 'CREDIT', // Acceptable values: CREDIT, DEBIT
      employeeID,
      invoiceNumber: String(invoice.number), // generated by POS unique ref number
      customerID: String(customer.customerID),
      softwareName: 'Erply Brazil POS',
      softwareVersion: version,
      referenceNumber: refno,
      ecrRefNum: '1',
      sigSavePath: uuidv4(),
      supportedPartialPayments: true,
      meta: {
        paymentIndex: index,
        totalPayments: total,
        isTapToPayPayment,
      },
    },
  });
};

const sendReturnPayment = ({
  payment,
  invoice,
  employeeID = '',
  customer,
  currencyCode,
  index,
  total,
}) => {
  const toOriginalFormat = p => {
    const payment = p.original ?? p;
    const getAttribute = name => {
      const attrs = (payment.original ?? payment).attributes ?? [];
      if (attrs.find) {
        return attrs.find(attr => attr?.attributeName === name)?.attributeValue;
      }
      if (attrs?.[name]) {
        return attrs?.[name];
      }
      return undefined;
    };

    return getAttribute('refNo')
      ? {
          cardType: String(payment.cardType),
          cardNumber: String(payment.cardNumber),
          cryptogram: String(payment.signature),
          referenceNumber: String(getAttribute('refNo')),
          transactionId: String(getAttribute('transId')),
          entryMode: String(getAttribute('entryMode')),
          amount: String(p.amount ?? payment.sum),
          dateTime: String(getAttribute('dateTime')),
        }
      : null;
  };
  const isTapToPayPayment = !!payment.attributes?.isTapToPayPayment;
  const uuid = uuidv4();

  sendMessage({
    type: 'payment:startReturn',
    requestID: uuid,
    payload: {
      employeeID,
      customerID: String(customer.customerID),
      currencyCode,
      amount: round(Math.abs(Number(payment.amount)), 2),
      transactionType: 'REFUND',
      invoiceNumber: String(invoice.number),
      original: toOriginalFormat(payment.original ?? payment),
      hostReferenceID: payment.attributes?.hostReferenceID ?? '',
      meta: {
        paymentIndex: index,
        totalPayments: total,
        isTapToPayPayment,
      },
    },
  });
};

/**
 * Send a regular/return payment request
 * @param {'SALE'|'PREAUTH'} transactionType Set this to preauth if you need to perform a capture later
 * @param {string} employeeID This should be a string, not an object
 * PREAUTH is not valid for returns!
 */
export const sendPayment = ({
  payment,
  employeeID = '',
  customer,
  currencyCode,
  transactionType,
  invoice,
  index,
  total,
}) =>
  Number(payment.amount) > 0
    ? sendRegularPayment({
        payment,
        employeeID,
        customer,
        currencyCode,
        transactionType,
        invoice,
        index,
        total,
      })
    : sendReturnPayment({
        payment,
        employeeID,
        customer,
        currencyCode,
        invoice,
        index,
        total,
      });
